import { memo, useRef, useState } from "react";
import { Pencil, Trash2, Undo2, Paperclip, AlertTriangle } from "lucide-react";
import { CURRENCIES, SUPPLY_OPTS, computeLine, fmtMoney, fmtNum, parseTr, supplyOf } from "@/lib/lineMath";

const focusCell = (key) => {
  const el = document.querySelector(`[data-cell="${key}"]`);
  if (el) { el.focus(); return true; }
  return false;
};

// Inline sayı hücresi — Enter: alttaki satır, Shift+Enter: üstteki, Tab: sonraki alan, Esc: iptal
export const EditCell = memo(function EditCell({ value, kind = "money", row, col, onCommit, disabled, changed, testId }) {
  const [txt, setTxt] = useState(null);
  const cancel = useRef(false);
  const justFocused = useRef(false);
  const display = kind === "int" ? String(value ?? 0) : fmtNum(value, kind === "pct" && Number.isInteger(Number(value)) ? 0 : 2);
  const commit = () => {
    if (txt === null) return;
    let v = parseTr(txt);
    if (kind === "int") v = Math.max(0, Math.round(v));
    if (kind === "pct") v = Math.min(100, Math.max(0, v));
    if (kind === "money") v = Math.max(0, v);
    if (v !== Number(value || 0)) onCommit(v);
  };
  return (
    <input
      data-cell={`${row}:${col}`} data-testid={testId} disabled={disabled} inputMode="decimal"
      value={txt ?? display}
      onFocus={(e) => { cancel.current = false; justFocused.current = true; setTxt(display); e.target.select(); }}
      onMouseUp={(e) => { if (justFocused.current) { e.preventDefault(); justFocused.current = false; } }}
      onChange={(e) => { justFocused.current = false; setTxt(e.target.value); }}
      onBlur={() => { if (!cancel.current) commit(); setTxt(null); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault(); commit(); setTxt(null);
          if (!focusCell(`${row + (e.shiftKey ? -1 : 1)}:${col}`)) e.target.blur();
        } else if (e.key === "Escape") {
          e.preventDefault(); cancel.current = true; setTxt(null); e.target.blur();
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault(); commit(); setTxt(null); focusCell(`${row + (e.key === "ArrowDown" ? 1 : -1)}:${col}`);
        }
      }}
      className={`w-full h-7 bg-transparent text-right font-mono text-[13px] tabular-nums px-1.5 rounded-[3px] border outline-none transition-colors
        ${changed ? "border-amber-500/40 bg-amber-500/[0.07] text-amber-100" : "border-transparent hover:border-slate-700"}
        focus:border-blue-500 focus:bg-slate-950 focus:text-white disabled:opacity-60 disabled:hover:border-transparent`}
    />
  );
});

// Tedarik hücresi: yöntem seçimi + Stoktan Satış'ta depo stok özeti / eksik uyarısı
export const SupplyCell = ({ row, stock, editable, changed, onSupply }) => {
  const m = supplyOf(row);
  const sq = Number(row.stock_qty || 0), q = Number(row.quantity || 0);
  const av = Number(stock?.available_physical || 0);
  const short = sq > 0 && !Number(row.reserved_qty || 0) ? Math.max(0, sq - av) : 0;
  return (
    <div className="min-w-[150px] max-w-[260px]">
      {editable ? (
        <select value={m} onChange={(e) => onSupply(row.id, e.target.value)} data-testid={`ws-supply-${row.id}`}
          className={`h-7 w-full bg-transparent text-[11px] rounded-[3px] border px-1 outline-none cursor-pointer focus:border-blue-500
            ${changed ? "border-amber-500/40 text-amber-100" : "border-transparent hover:border-slate-700 text-slate-300"}`}>
          <option value="" className="bg-slate-900">Ürün varsayılanı</option>
          {SUPPLY_OPTS.map(([v, l]) => <option key={v} value={v} className="bg-slate-900">{l}</option>)}
        </select>
      ) : (
        <span className="text-[11px] text-slate-300" data-testid={`ws-supply-${row.id}`}>
          {(SUPPLY_OPTS.find(([v]) => v === m) || [, "—"])[1]}
        </span>
      )}
      {sq > 0 && (
        <div className="text-[10px] font-mono leading-tight text-slate-500 mt-0.5" data-testid={`ws-stockinfo-${row.id}`}>
          {row.warehouse_name || "Depo yok"} · Fiz {fmtNum(stock?.physical, 0)} · Rez {fmtNum(stock?.reserved_physical, 0)} · Kul {fmtNum(av, 0)}
          {sq < q && <span className="text-slate-400"> · {sq} stok + {q - sq} {row.remaining_supply_method === "internal" ? "İç Üretim" : row.remaining_supply_method}</span>}
          {Number(row.reserved_qty || 0) > 0 && <span className="text-emerald-400"> · {row.reserved_qty} rezerveli</span>}
          {short > 0 && <div className="text-amber-400" data-testid={`ws-stockshort-${row.id}`}>⚠ STOK YETERSİZ – {short} ADET EKSİK</div>}
        </div>
      )}
    </div>
  );
};

// Depo hücresi: tenant depoları + bu varyantın depo bazlı kullanılabilir stoğu (mevcut stock-availability)
export const WarehouseCell = ({ row, warehouses, stockMap, editable, changed, onPick }) => {
  const sq = Number(row.stock_qty || 0);
  const avOf = (wid) => stockMap?.[`${row.product_id}|${row.variant_id || ""}|${wid}`]?.available_physical;
  const locked = Number(row.reserved_qty || 0) > 0 || Number(row.reserved_incoming_qty || 0) > 0;
  const cur = avOf(row.warehouse_id || "");
  const short = sq > 0 && !locked && row.warehouse_id && Number(cur || 0) < sq;
  if (!editable || locked) {
    return (
      <span className="text-[11px] font-mono text-slate-400 whitespace-nowrap" data-testid={`ws-wh-${row.id}`}
        title={locked ? "Rezerveli satırın deposu değiştirilemez" : undefined}>
        {row.warehouse_name || "—"}{locked && " 🔒"}
      </span>
    );
  }
  return (
    <div className="min-w-[190px] max-w-[250px]">
      <select value={row.warehouse_id || ""} data-testid={`ws-wh-${row.id}`}
        onChange={(e) => { const w = warehouses.find((x) => x.id === e.target.value); onPick(row.id, w); }}
        className={`h-7 w-full bg-transparent text-[11px] rounded-[3px] border px-1 outline-none cursor-pointer focus:border-blue-500
          ${changed ? "border-amber-500/40 text-amber-100" : sq > 0 && !row.warehouse_id ? "border-rose-500/60 text-rose-300" : "border-transparent hover:border-slate-700 text-slate-300"}`}>
        <option value="" className="bg-slate-900">{sq > 0 ? "Depo seçin" : "—"}</option>
        {warehouses.map((w) => {
          const a = avOf(w.id);
          return <option key={w.id} value={w.id} className="bg-slate-900">{w.name}{a != null ? ` — Kullanılabilir: ${fmtNum(a, 0)}` : " — Stok yok"}</option>;
        })}
      </select>
      {short && <div className="text-[10px] text-amber-400 font-mono" data-testid={`ws-wh-short-${row.id}`}>⚠ Bu depoda yetersiz: {fmtNum(cur || 0, 0)} / {sq}</div>}
      {sq > 0 && !row.warehouse_id && <div className="text-[10px] text-rose-400" data-testid={`ws-wh-missing-${row.id}`}>Stoktan Satış için depo seçin</div>}
    </div>
  );
};

// Gerçek beden: backend'in varyant kaydından verdiği `size` → yoksa varyant nitelik kopyası; SKU'dan türetilmez
const sizeOf = (l) => {
  if (l.size) return l.size;
  const a = l.variant_attributes || {};
  const k = Object.keys(a).find((x) => /^\s*(beden|size)\s*$/i.test(x));
  return (k && a[k]) || "—";
};

export const LineRow = memo(function LineRow({
  row, orig, idx, quick, selected, changedSig, readOnly, discLimit, stock, supplyEditable, onSupply,
  warehouses, stockMap, whEditable, onWarehouse,
  onPatch, onCurrency, onToggle, onDetail, onDelete, onUndoDelete,
}) {
  const changed = changedSig ? changedSig.split(",") : [];
  const t = computeLine(row);
  const ch = (k) => changed.includes(k);
  const isNew = changed.includes("new");
  const dirty = changed.length > 0;
  const del = row._deleted;
  const ro = readOnly || del;
  const gross = Number(row.quantity || 0) * Number(row.unit_price || 0);
  const effDisc = Number(row.discount_percent || 0) + (gross > 0 ? (Number(row.discount_amount || 0) / gross) * 100 : 0);
  const cell = "px-1.5 py-1 align-middle";
  const num = `${cell} text-right font-mono text-[13px] tabular-nums`;
  const id = row.id;
  return (
    <tr data-testid={`ws-row-${id}`} data-dirty={dirty ? "1" : "0"}
      className={`group border-b border-slate-800/70 transition-colors ${del ? "opacity-45" : ""}
        ${selected ? "bg-blue-500/[0.08]" : dirty ? "bg-amber-500/[0.03]" : "hover:bg-slate-900/60"}`}>
      <td className={`${cell} w-9 relative`}>
        <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${isNew ? "bg-emerald-500" : del ? "bg-rose-500" : dirty ? "bg-amber-400" : "bg-transparent"}`} />
        <input type="checkbox" checked={selected} disabled={readOnly} onChange={() => onToggle(id)}
          data-testid={`ws-select-${id}`} className="accent-blue-500 w-3.5 h-3.5 cursor-pointer" />
      </td>
      <td className={`${cell} min-w-[240px]`}>
        <div className={`text-[13px] text-slate-100 leading-tight truncate max-w-[420px] ${del ? "line-through" : ""}`} title={row.product_name}>
          {row.product_name}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500 leading-tight mt-0.5">
          {row.variant_sku && <span>{row.variant_sku}</span>}
          {isNew && <span className="text-emerald-400 uppercase tracking-wider" data-testid={`ws-new-${id}`}>yeni</span>}
          {del && <span className="text-rose-400 uppercase tracking-wider" data-testid={`ws-deleted-${id}`}>silinecek</span>}
          {!isNew && !del && dirty && <span className="text-amber-400/90 uppercase tracking-wider" data-testid={`ws-changed-${id}`}>değiştirildi</span>}
          {(row.applications || []).length > 0 && <span className="text-orange-400">+{row.applications.length} uyg.</span>}
        </div>
      </td>
      <td className={`${cell} text-[12px] font-mono text-emerald-400 whitespace-nowrap`} data-testid={`ws-size-${id}`}>{sizeOf(row)}</td>
      {!quick && (
        <td className={cell}>
          <WarehouseCell row={row} warehouses={warehouses} stockMap={stockMap} editable={whEditable && !ro}
            changed={ch("warehouse_id")} onPick={onWarehouse} />
        </td>
      )}
      {!quick && (
        <td className={cell}>
          <SupplyCell row={row} stock={stock} editable={supplyEditable && !ro} changed={ch("stock_qty") || ch("remaining_supply_method")} onSupply={onSupply} />
        </td>
      )}
      <td className={`${num} w-[72px]`}>
        <EditCell kind="int" value={row.quantity} row={idx} col="qty" disabled={ro} changed={ch("quantity")}
          testId={`ws-qty-${id}`} onCommit={(v) => onPatch(id, { quantity: v })} />
      </td>
      <td className={`${cell} w-[70px]`}>
        <select value={row.currency || "TRY"} disabled={ro} data-testid={`ws-cur-${id}`}
          onChange={(e) => onCurrency([id], e.target.value)}
          className={`h-7 w-full bg-transparent font-mono text-[12px] rounded-[3px] border px-1 outline-none cursor-pointer focus:border-blue-500
            ${ch("currency") ? "border-amber-500/40 text-amber-100" : "border-transparent hover:border-slate-700 text-slate-300"}`}>
          {CURRENCIES.map((c) => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
        </select>
      </td>
      {quick && (
        <td className={`${num} w-[110px] text-slate-500`} data-testid={`ws-oldprice-${id}`}>
          {orig ? fmtMoney(orig.unit_price, orig.currency || "TRY") : "—"}
        </td>
      )}
      <td className={`${num} w-[128px]`}>
        {!quick && ch("unit_price") && orig && (
          <div className="text-[10px] text-slate-500 line-through leading-none pr-1.5 mb-0.5" data-testid={`ws-was-${id}`}>
            {fmtMoney(orig.unit_price, orig.currency || "TRY")}
          </div>
        )}
        <EditCell kind="money" value={row.unit_price} row={idx} col="price" disabled={ro} changed={ch("unit_price")}
          testId={`ws-price-${id}`} onCommit={(v) => onPatch(id, { unit_price: v })} />
      </td>
      <td className={`${num} w-[74px]`}>
        <div className="relative">
          <EditCell kind="pct" value={row.discount_percent} row={idx} col="disc" disabled={ro} changed={ch("discount_percent")}
            testId={`ws-disc-${id}`} onCommit={(v) => onPatch(id, { discount_percent: v })} />
          {effDisc > discLimit + 1e-9 && (
            <AlertTriangle className="w-3 h-3 text-amber-400 absolute -left-3.5 top-2" data-testid={`ws-disc-warn-${id}`}>
              <title>{`Efektif iskonto %${effDisc.toFixed(1)} — limit %${discLimit}`}</title>
            </AlertTriangle>
          )}
        </div>
      </td>
      <td className={`${num} w-[66px]`}>
        <EditCell kind="pct" value={row.tax_rate} row={idx} col="tax" disabled={ro} changed={ch("tax_rate")}
          testId={`ws-tax-${id}`} onCommit={(v) => onPatch(id, { tax_rate: v })} />
      </td>
      {!quick && <td className={`${num} w-[120px] text-slate-400`} data-testid={`ws-sub-${id}`}>{fmtMoney(t.line_subtotal, row.currency)}</td>}
      <td className={`${num} w-[130px] font-semibold ${dirty ? "text-amber-100" : "text-slate-100"}`} data-testid={`ws-total-${id}`}>
        {fmtMoney(t.line_total, row.currency)}
      </td>
      {!quick && (
        <td className={`${cell} w-[92px] text-right whitespace-nowrap`}>
          {(row.attachments || []).length > 0 && (
            <span className="inline-flex items-center text-[10px] text-slate-500 mr-1 font-mono"><Paperclip className="w-3 h-3" />{row.attachments.length}</span>
          )}
          {del ? (
            <button type="button" onClick={() => onUndoDelete(id)} data-testid={`ws-undo-${id}`}
              className="h-7 px-1.5 text-slate-300 hover:text-white"><Undo2 className="w-3.5 h-3.5" /></button>
          ) : (
            <>
              <button type="button" onClick={() => onDetail(id)} data-testid={`edit-line-${id}`} title="Detaylı düzenle"
                className="h-7 px-1.5 text-slate-400 hover:text-blue-400"><Pencil className="w-3.5 h-3.5" /></button>
              {!readOnly && (
                <button type="button" onClick={() => onDelete(id)} data-testid={`delete-line-${id}`} title="Satırı sil"
                  className="h-7 px-1.5 text-slate-500 hover:text-rose-400 opacity-60 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </>
          )}
        </td>
      )}
    </tr>
  );
});
